import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { formatCurrentYear } from "@/lib/format";

export const Footer = () => {
  return (
    <footer className="relative border-t border-border bg-background-deep">
      <div className="container-page py-20">
        <div className="grid gap-12 md:grid-cols-12 items-center">
          <div className="md:col-span-4 space-y-6">
            <Logo />
            <p className="text-sm text-foreground/65 max-w-xs leading-relaxed">
              Redovisningsbyrå i Umeå och Stockholm med digital bokföring, löneadministration,
              bokslut, deklaration och rådgivning för företag i hela Sverige.
            </p>
            <div className="space-y-2 text-sm">
              <a href="tel:0703036345" className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors">
                <Phone className="h-4 w-4" /> 070-303 63 45
              </a>
              <a href="mailto:info@cloudredovisning.se" className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors">
                <Mail className="h-4 w-4" /> info@cloudredovisning.se
              </a>
              <div className="flex items-start gap-2 text-foreground/80">
                <MapPin className="mt-0.5 h-4 w-4" />
                <span>Umeå, Stockholm, Vännäs och digitalt i hela Sverige</span>
              </div>
            </div>
          </div>

          <FooterCol
            title="Tjänster"
            items={[
              { to: "/bokforing", label: "Löpande bokföring" },
              { to: "/bokslut", label: "Bokslut & deklaration" },
              { to: "/radgivning", label: "Rådgivning & tillväxt" },
              { to: "/inhouse", label: "Inhouse-medarbetare" },
              { to: "/loneadministration", label: "Löneadministration" },
              { to: "/arsredovisning-aktiebolag", label: "Årsredovisning aktiebolag" },
              { to: "/priser", label: "Priser" },
              { to: "/byta-redovisningsbyra", label: "Byta redovisningsbyrå" },
            ]}
          />
          <FooterCol
            title="Företag"
            items={[
              { to: "/om-oss", label: "Om oss" },
              { to: "/karriar", label: "Karriär" },
              { to: "/kundreferenser", label: "Referenser" },
              { to: "/nyheter", label: "Nyheter" },
              { to: "/medarbetare", label: "Medarbetare" },
            ]}
          />
          <FooterCol
            title="Kontor"
            items={[
              { to: "/kontakt-umea", label: "Umeå" },
              { to: "/kontakt-stockholm", label: "Stockholm" },
              { to: "/bokforing-umea", label: "Bokföring Umeå" },
              { to: "/bokforing-stockholm", label: "Bokföring Stockholm" },
              { to: "/kontakt", label: "Alla kontaktvägar" },
            ]}
            extra={
              <div className="mt-4 space-y-3 text-sm text-foreground/70">
                <p>Umeå: Pendelgatan 3b, 904 22 Umeå</p>
                <p>Stockholm: Arenavägen 45, plan 16, 121 77 Johanneshov</p>
                <a
                  href="https://portal.cloudredovisning.se/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/8 px-4 py-2.5 font-medium text-primary shadow-[0_10px_24px_-18px_rgba(31,168,232,0.9)] transition-all hover:border-primary/35 hover:bg-primary/12"
                >
                  Kundportal <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            }
          />
        </div>

        <div className="hairline mt-16" />
        <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between text-xs text-foreground-muted">
          <p>© {formatCurrentYear()} Cloud Redovisning & Administration. Alla rättigheter förbehållna.</p>
          <div className="flex items-center gap-6">
            <Link to="/integritetspolicy" className="hover:text-foreground transition-colors">Integritetspolicy</Link>
            <span>Auktoriserad av FAR</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({
  title,
  items,
  extra,
}: {
  title: string;
  items: { to: string; label: string }[];
  extra?: React.ReactNode;
}) => (
  <div className="md:col-span-2 lg:col-span-2">
    <div className="text-xs uppercase tracking-[0.2em] text-primary mb-4">{title}</div>
    <ul className="space-y-2.5">
      {items.map((it) => (
        <li key={it.to}>
          <Link to={it.to} className="text-sm text-foreground/75 hover:text-foreground transition-colors">
            {it.label}
          </Link>
        </li>
      ))}
    </ul>
    {extra}
  </div>
);
